import React, { useEffect, useState } from "react";

import { Button, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Alert from "@material-ui/lab/Alert";
import { Confirm, useGetOne } from "react-admin";
import { useHistory, useLocation } from "react-router-dom";

import { useUser } from "../../authenticatorApp/UserContext";
import { AclType } from "../../utils/constants";
import { COMPANY_PATH } from "../carrierPaths";

/**
 * A notice under the app bar while the company profile lists no regulatory
 * number, pointing at the company profile where one is entered, plus a dialog
 * that puts the same request in front of the page every third session.
 *
 * A number is what lets two yards' entries be recognised as one company
 * (CarrierIdentifier in the schema), and the company profile is opened rarely —
 * it sits behind the user menu — so a company created without one tends to stay
 * that way. The notice sits in the layout rather than on any one page so it is
 * seen whichever screen the session lands on. A notice above the page is easy
 * to scroll past for months, which is why the dialog exists; both stop the
 * moment a number is entered.
 *
 * Shown only to sessions holding User Management: that is the portal's "acts for
 * the company" permission, and for anyone else the profile is read-only, so the
 * notice would point at a field they cannot fill.
 */

const NOTICE =
  "Your company profile lists no carrier ID number (USDOT, MC, …). " +
  "Add one so we can recognise your company.";

const REMINDER_TITLE = "Add your carrier ID number";

const REMINDER_CONTENT =
  "Your company profile lists no carrier ID number (USDOT, MC, …). " +
  "It is what lets the transloads you work with recognise your company as " +
  "one business rather than an entry per yard. Adding one takes a moment.";

// Sessions between dialogs. Every third rather than every one: a request that
// interrupts every sign-in is answered by learning to dismiss it.
const REMIND_EVERY_NTH_SESSION = 3;

// Keyed by carrier: a shared office machine signs in more than one company, and
// each company's tally is its own. The running count lives in localStorage so it
// survives the browser closing; the mark saying this session is already counted
// lives in sessionStorage, whose lifetime is the definition of a session we
// want.
const SESSION_COUNT_KEY = "carrierIdReminderSessions";
const SESSION_COUNTED_KEY = "carrierIdReminderCounted";

const hasIdentifiers = (record) =>
  Array.isArray(record?.identifiers) && record.identifiers.length > 0;

/**
 * The number of this session, or null if it has already been counted.
 *
 * Counting happens only where this is called — inside the gate, so only sessions
 * that actually see the notice are tallied and a company that entered a number
 * never advances towards another dialog. Returning null on the second call is
 * what keeps the dialog to one appearance per sign-in however often the layout
 * remounts on the way round the portal.
 */
const countSession = (carrierId) => {
  const countedKey = `${SESSION_COUNTED_KEY}:${carrierId}`;
  if (sessionStorage.getItem(countedKey)) return null;
  const countKey = `${SESSION_COUNT_KEY}:${carrierId}`;
  const count = (Number(localStorage.getItem(countKey)) || 0) + 1;
  localStorage.setItem(countKey, String(count));
  sessionStorage.setItem(countedKey, "1");
  return count;
};

const BannerAlert = ({ onDismiss }) => {
  const history = useHistory();
  return (
    <Alert
      severity="info"
      style={{ marginBottom: 8 }}
      action={
        <>
          <Button
            color="inherit"
            size="small"
            onClick={() => history.push(COMPANY_PATH)}
          >
            Add ID
          </Button>
          <IconButton
            aria-label="close"
            color="inherit"
            size="small"
            onClick={onDismiss}
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </>
      }
    >
      {NOTICE}
    </Alert>
  );
};

// Mounted only inside the gate, so the session is counted and the dialog
// considered only while the number is still missing. Cancel closes it until the
// third session after this one; Edit Profile lands on the page that fills it in.
const ReminderDialog = ({ carrierId }) => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const count = countSession(carrierId);
    if (count !== null && count % REMIND_EVERY_NTH_SESSION === 0)
      setIsOpen(true);
  }, [carrierId]);

  const handleClose = () => setIsOpen(false);

  return (
    <Confirm
      isOpen={isOpen}
      title={REMINDER_TITLE}
      content={REMINDER_CONTENT}
      confirm="Edit Profile"
      cancel="Cancel"
      onConfirm={() => {
        setIsOpen(false);
        history.push(COMPANY_PATH);
      }}
      onClose={handleClose}
    />
  );
};

// The company record rather than the mirrored entry once the entry is placed:
// saving the profile writes the company, which react-admin's store reflects at
// once, while mirroring back down to the yard entries is the stream a moment
// later. Reading the company makes the notice disappear on save rather than on
// the next sign-in.
const CompanyIdentifierGate = ({ companyId, children }) => {
  const {
    data: company,
    loading,
    error,
  } = useGetOne("carrierCompanys", companyId);
  if (loading || error || !company || hasIdentifiers(company)) return null;
  return children;
};

// The session names a yard entry, not a company, so the entry is read first for
// the companyId the stream stamped on it — the same two-step the company profile
// makes. An entry the stream has not placed yet holds its own numbers, and is
// then also what the profile's save writes, so it answers the question alone.
const EntryIdentifierGate = ({ carrierId, children }) => {
  const { data: entry, loading, error } = useGetOne("carriers", carrierId);
  if (loading || error || !entry) return null;
  if (entry.companyId)
    return (
      <CompanyIdentifierGate companyId={entry.companyId}>
        {children}
      </CompanyIdentifierGate>
    );
  return hasIdentifiers(entry) ? null : children;
};

export const MissingCarrierIdBanner = () => {
  const { appUser, isAuthorized } = useUser();
  const location = useLocation();
  // Dismissal lasts the session: the layout stays mounted across navigation, so
  // the notice stays away until the next sign-in rather than the next click.
  // Only the alert goes — the dialog is the louder reminder the dismissal is
  // trying to escape, and it has its own once-a-session limit.
  const [dismissed, setDismissed] = useState(false);

  const carrierId = appUser?.carrierId;
  if (!carrierId || !isAuthorized(AclType.USERS)) return null;
  // Not on the company profile itself: the page the notice points at already
  // shows the empty identifiers form.
  if (location.pathname === COMPANY_PATH) return null;

  return (
    <EntryIdentifierGate carrierId={carrierId}>
      <>
        {!dismissed && <BannerAlert onDismiss={() => setDismissed(true)} />}
        <ReminderDialog carrierId={carrierId} />
      </>
    </EntryIdentifierGate>
  );
};
